/**
 * coachRoutes.ts — pure route matching for the guided tour.
 *
 * Tour route patterns are authored in each tour's Markdown frontmatter
 * (`routes: /apps/:id`) and compiled into `TOUR_ROUTES` in
 * tours.generated.ts, already ordered static-first / longer / alpha so the
 * first match here is always the most specific one (see
 * scripts/build-tours.js buildRoutesTable). No React, no router import —
 * every input is a plain string so this can be unit-tested without jsdom
 * and reused by both `lib/coachTours.ts` and the Provider's route effects.
 */

/** Strips the query string, hash, and a trailing slash (but keeps a bare '/'). */
export function normalizePath(pathname: string): string {
  let p = pathname.split('?')[0].split('#')[0];
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p || '/';
}

/**
 * `*` matches any path. Otherwise the pattern and the path must have the
 * same number of segments; each pattern segment either matches literally or,
 * if it starts with `:`, matches any single non-empty segment.
 */
export function matchRoutePattern(pattern: string, pathname: string): boolean {
  if (pattern === '*') return true;

  const path = normalizePath(pathname);
  const patternSegments = pattern.split('/').filter(Boolean);
  const pathSegments = path.split('/').filter(Boolean);
  if (patternSegments.length !== pathSegments.length) return false;

  for (let i = 0; i < patternSegments.length; i++) {
    const patSeg = patternSegments[i];
    const pathSeg = pathSegments[i];
    if (patSeg.startsWith(':')) {
      if (!pathSeg) return false;
    } else if (patSeg !== pathSeg) {
      return false;
    }
  }
  return true;
}

/** First route entry (in the given order) whose pattern matches `pathname`. */
export function tourIdForPath(
  pathname: string,
  routes: ReadonlyArray<{ pattern: string; tourId: string }>
): string | null {
  for (const route of routes) {
    if (matchRoutePattern(route.pattern, pathname)) return route.tourId;
  }
  return null;
}
